import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, ShieldCheck, ArrowRight, ArrowLeft } from "lucide-react";
import { Reveal, Eyebrow, EASE } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BUDGETS = [
  "Moins de 10 000 €",
  "10 000 € — 25 000 €",
  "25 000 € — 50 000 €",
  "50 000 € — 100 000 €",
  "100 000 € — 250 000 €",
  "Plus de 250 000 €",
];
const ETATS = ["Neuf / jamais portée", "Excellent état", "Vintage", "Indifférent"];
const DELAIS = ["Dès que possible", "1 à 3 mois", "Recherche sans urgence"];

const EMPTY = {
  prenom: "", nom: "", email: "", telephone: "", marque: "", modele: "", reference: "",
  reference_inconnue: false, budget: "", etat: "", annee: "", configuration: "", delai: "",
  message: "", consent: false,
};

const STEPS = [
  { n: "01", title: "Quelle montre ?", sub: "Décrivez le garde-temps — référence exacte ou simple envie." },
  { n: "02", title: "Budget & délai", sub: "Pour orienter la recherche au sein de notre réseau." },
  { n: "03", title: "Vous joindre", sub: "Un interlocuteur unique vous répond personnellement." },
];

const Field = ({ label, children, htmlFor, className = "" }) => (
  <div className={className}>
    <label htmlFor={htmlFor} className="lux-label">{label}</label>
    {children}
  </div>
);

const Select = ({ id, value, onChange, options, testid }) => (
  <select id={id} className="lux-input bg-obsidian" value={value} onChange={onChange} data-testid={testid}>
    <option value="">Sélectionner</option>
    {options.map((o) => <option key={o} value={o}>{o}</option>)}
  </select>
);

const StepWatch = ({ form, set }) => (
  <div className="grid sm:grid-cols-2 gap-x-10 gap-y-8">
    <Field label="Marque recherchée *" htmlFor="marque">
      <input id="marque" placeholder="Rolex, Patek Philippe, Audemars Piguet…" className="lux-input" value={form.marque} onChange={set("marque")} data-testid="form-marque" />
    </Field>
    <Field label="Modèle" htmlFor="modele">
      <input id="modele" placeholder="Submariner, Nautilus, Royal Oak…" className="lux-input" value={form.modele} onChange={set("modele")} data-testid="form-modele" />
    </Field>
    <Field label="Référence" htmlFor="reference">
      <input id="reference" placeholder={form.reference_inconnue ? "Décrivez-la ci-dessous" : "Ex : 126610LN"} className="lux-input disabled:opacity-40" value={form.reference} onChange={set("reference")} disabled={form.reference_inconnue} data-testid="form-reference" />
    </Field>
    <Field label="Bracelet / cadran / configuration" htmlFor="configuration">
      <input id="configuration" placeholder="Ex : cadran bleu, bracelet Oyster…" className="lux-input" value={form.configuration} onChange={set("configuration")} data-testid="form-configuration" />
    </Field>
    <Field label="État souhaité" htmlFor="etat">
      <Select id="etat" value={form.etat} onChange={set("etat")} options={ETATS} testid="form-etat" />
    </Field>
    <Field label="Année souhaitée" htmlFor="annee">
      <input id="annee" placeholder="Ex : 2023, indifférent…" className="lux-input" value={form.annee} onChange={set("annee")} data-testid="form-annee" />
    </Field>
    <label className="sm:col-span-2 mt-2 flex items-center gap-3 cursor-pointer group" data-testid="form-reference-inconnue-label">
      <input type="checkbox" checked={form.reference_inconnue} onChange={set("reference_inconnue")} className="w-4 h-4 accent-[#C5A059]" data-testid="form-reference-inconnue" />
      <span className="text-sm text-stone2 group-hover:text-ivory transition-colors">
        Je ne connais pas la référence exacte — je décris simplement ce que je recherche.
      </span>
    </label>
  </div>
);

const StepBudget = ({ form, set }) => (
  <div className="grid sm:grid-cols-2 gap-x-10 gap-y-8">
    <Field label="Budget *" htmlFor="budget">
      <Select id="budget" value={form.budget} onChange={set("budget")} options={BUDGETS} testid="form-budget" />
    </Field>
    <Field label="Délai souhaité" htmlFor="delai">
      <Select id="delai" value={form.delai} onChange={set("delai")} options={DELAIS} testid="form-delai" />
    </Field>
    <Field label="Message complémentaire" htmlFor="message" className="sm:col-span-2">
      <textarea id="message" rows={4} className="lux-input resize-none" placeholder="Vos envies, vos contraintes, le contexte de votre recherche…" value={form.message} onChange={set("message")} data-testid="form-message" />
    </Field>
  </div>
);

const StepContact = ({ form, set }) => (
  <div className="grid sm:grid-cols-2 gap-x-10 gap-y-8">
    <Field label="Prénom *" htmlFor="prenom">
      <input id="prenom" className="lux-input" value={form.prenom} onChange={set("prenom")} data-testid="form-prenom" />
    </Field>
    <Field label="Nom *" htmlFor="nom">
      <input id="nom" className="lux-input" value={form.nom} onChange={set("nom")} data-testid="form-nom" />
    </Field>
    <Field label="Adresse e-mail *" htmlFor="email">
      <input id="email" type="email" className="lux-input" value={form.email} onChange={set("email")} data-testid="form-email" />
    </Field>
    <Field label="Téléphone" htmlFor="telephone">
      <input id="telephone" type="tel" className="lux-input" value={form.telephone} onChange={set("telephone")} data-testid="form-telephone" />
    </Field>
    <div className="sm:col-span-2 border border-white/[0.08] bg-white/[0.02] p-5 text-sm text-stone2 leading-relaxed" data-testid="form-recap">
      <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-gold/70 mb-3">Votre recherche</p>
      <p className="text-ivory">
        {[form.marque, form.modele, form.reference_inconnue ? "référence à préciser" : form.reference].filter(Boolean).join(" · ") || "—"}
      </p>
      <p className="mt-1">{[form.configuration, form.etat, form.annee, form.budget, form.delai].filter(Boolean).join(" · ")}</p>
    </div>
    <label className="sm:col-span-2 flex items-start gap-3 cursor-pointer group">
      <input type="checkbox" checked={form.consent} onChange={set("consent")} className="w-4 h-4 mt-0.5 accent-[#C5A059]" data-testid="form-consent" />
      <span className="text-xs text-stone2 leading-relaxed group-hover:text-ivory/80 transition-colors">
        J'accepte que mes informations soient utilisées uniquement pour traiter ma demande, conformément à la{" "}
        <a href="/politique-confidentialite" target="_blank" rel="noreferrer" className="text-gold underline underline-offset-4">politique de confidentialité</a>. *
      </span>
    </label>
  </div>
);

const STEP_VIEWS = [StepWatch, StepBudget, StepContact];

const validate = (step, form) => {
  if (step === 0 && !form.marque.trim()) return "Indiquez au moins la marque recherchée.";
  if (step === 1 && !form.budget) return "Sélectionnez une fourchette de budget.";
  if (step === 2) {
    if (!form.prenom.trim() || !form.nom.trim()) return "Merci d'indiquer votre prénom et votre nom.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return "Merci d'indiquer une adresse e-mail valide.";
    if (!form.consent) return "Merci d'accepter la politique de confidentialité pour envoyer votre demande.";
  }
  return null;
};

export default function RequestForm() {
  const [form, setForm] = useState(EMPTY);
  const [step, setStep] = useState(0);
  const [dir, setDir] = useState(1);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const next = () => {
    const err = validate(step, form);
    if (err) return toast.error(err);
    setDir(1);
    setStep((s) => s + 1);
  };
  const prev = () => { setDir(-1); setStep((s) => s - 1); };

  const submit = async (e) => {
    e.preventDefault();
    const err = validate(2, form);
    if (err) return toast.error(err);
    setSending(true);
    try {
      await axios.post(`${API}/requests`, form);
      toast.success("Votre demande a bien été transmise. Un e-mail de confirmation vient de vous être envoyé.");
      setSent(true);
    } catch {
      toast.error("Une erreur est survenue. Merci de réessayer dans un instant.");
    } finally {
      setSending(false);
    }
  };

  const Step = STEP_VIEWS[step];
  const last = step === STEPS.length - 1;

  return (
    <section id="demande" className="relative py-32 lg:py-48 bg-coal/30" data-testid="request-form-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-14 lg:gap-24">
        <div className="lg:col-span-4">
          <Reveal>
            <Eyebrow className="!tracking-[0.55em]">Demande privée</Eyebrow>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-8 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-ivory">
              Confiez-nous
              <br />
              <span className="italic text-gold">votre recherche.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.2} className="mt-10 space-y-6">
            {STEPS.map((s, i) => (
              <button
                type="button"
                key={s.n}
                onClick={() => i < step && setStep(i)}
                className={`flex gap-5 text-left transition-opacity duration-500 ${i === step ? "opacity-100" : "opacity-40"}`}
                data-testid={`form-step-indicator-${i}`}
              >
                <span className="font-mono text-[10px] tracking-[0.3em] text-gold pt-1">{s.n}</span>
                <span>
                  <span className="block font-display text-xl text-ivory">{s.title}</span>
                  <span className="block mt-1 text-xs text-stone2">{s.sub}</span>
                </span>
              </button>
            ))}
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-12 flex items-start gap-4 text-sm text-ivory/70 leading-relaxed" data-testid="form-reassurance">
              <ShieldCheck size={20} strokeWidth={1.2} className="text-gold shrink-0 mt-0.5" />
              <p>Chaque demande est étudiée individuellement. Vos informations restent strictement confidentielles.</p>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-8">
          <Reveal delay={0.15}>
            <form onSubmit={submit} className="relative border border-white/[0.08] bg-obsidian p-7 sm:p-10 lg:p-14 gold-frame min-h-[520px] flex flex-col" data-testid="request-form">
              <div className="absolute top-0 left-0 h-px bg-white/[0.08] w-full">
                <motion.div className="h-full bg-gold" animate={{ width: `${((step + 1) / STEPS.length) * 100}%` }} transition={{ duration: 0.8, ease: EASE }} />
              </div>

              {sent ? (
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: EASE }} className="my-auto text-center py-10" data-testid="form-success">
                  <p className="font-mono text-[10px] tracking-[0.4em] uppercase text-gold">Demande reçue</p>
                  <h3 className="mt-6 font-display text-3xl sm:text-4xl text-ivory">Merci, {form.prenom}.</h3>
                  <p className="mt-5 text-stone2 max-w-md mx-auto leading-relaxed">
                    Votre recherche est entre nos mains. Un e-mail de confirmation vous a été adressé ; nous revenons vers vous personnellement sous 48 heures.
                  </p>
                </motion.div>
              ) : (
                <>
                  <div className="flex items-baseline justify-between mb-10">
                    <h3 className="font-display text-2xl sm:text-3xl text-ivory" data-testid="form-step-title">{STEPS[step].title}</h3>
                    <span className="font-mono text-[10px] tracking-[0.3em] text-stone2/70">{STEPS[step].n} / 0{STEPS.length}</span>
                  </div>
                  <div className="flex-1 overflow-hidden">
                    <AnimatePresence mode="wait" initial={false} custom={dir}>
                      <motion.div
                        key={step}
                        initial={{ opacity: 0, x: dir * 40 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: dir * -40 }}
                        transition={{ duration: 0.6, ease: EASE }}
                      >
                        <Step form={form} set={set} />
                      </motion.div>
                    </AnimatePresence>
                  </div>
                  <div className="mt-12 flex items-center justify-between gap-6">
                    {step > 0 ? (
                      <button type="button" onClick={prev} className="inline-flex items-center gap-3 text-[11px] tracking-[0.25em] uppercase text-stone2 hover:text-ivory transition-colors" data-testid="form-prev">
                        <ArrowLeft size={14} strokeWidth={1.5} /> Retour
                      </button>
                    ) : <span />}
                    {last ? (
                      <button type="submit" disabled={sending} className="group relative bg-gold text-obsidian px-9 py-4 text-[12px] tracking-[0.25em] uppercase font-medium overflow-hidden disabled:opacity-60" data-testid="form-submit">
                        <span className="relative z-10 flex items-center gap-3">
                          {sending && <Loader2 size={15} className="animate-spin" />}
                          {sending ? "Envoi en cours…" : "Envoyer ma demande"}
                        </span>
                        <span className="absolute inset-0 bg-gold-light translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-out" />
                      </button>
                    ) : (
                      <button type="button" onClick={next} className="group inline-flex items-center gap-4 border border-gold/60 text-gold px-8 py-4 text-[12px] tracking-[0.25em] uppercase hover:bg-gold hover:text-obsidian transition-all duration-500" data-testid="form-next">
                        Continuer <ArrowRight size={14} strokeWidth={1.5} className="transition-transform duration-500 group-hover:translate-x-1" />
                      </button>
                    )}
                  </div>
                </>
              )}
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
